import type {
  CalculateOffersPersonResult,
  CalculateOffersResponse,
  OfferItem,
} from '@/services/inventory-api.service';

function aggregateOfferItems(items: OfferItem[]): OfferItem[] {
  const map = new Map<string, OfferItem>();
  for (const item of items) {
    const pk = item.pass_pk != null ? String(item.pass_pk) : '';
    const ct = (item.customer_type ?? '').trim().toLowerCase();
    const tt = (item.title ?? '').trim().toLowerCase();
    const key = `${pk}|${ct}|${tt}`;
    const existing = map.get(key);
    if (existing) {
      const q1 = Math.max(0, Math.trunc(existing.quantity));
      const q2 = Math.max(0, Math.trunc(item.quantity));
      existing.quantity = q1 + q2;
      existing.price += item.price;
    } else {
      map.set(key, { ...item });
    }
  }
  return [...map.values()];
}

function collectItemsFromPeople(people: CalculateOffersPersonResult[] | undefined): OfferItem[] {
  if (!Array.isArray(people)) return [];
  const collected: OfferItem[] = [];
  for (const person of people) {
    if (person.error) continue;
    const rowItems = person.items ?? [];
    for (const item of rowItems) {
      collected.push(item);
    }
  }
  return collected;
}

export function isPurchasableOfferItem(item: OfferItem): boolean {
  const qty = Math.max(0, Math.trunc(item.quantity));
  if (qty <= 0) return false;
  const hasPrice = typeof item.price === 'number' && item.price > 0;
  if (hasPrice) return true;
  if (item.pass_pk != null) return true;
  const title = (item.title ?? '').trim();
  const customerType = (item.customer_type ?? '').trim();
  return title.length > 0 && customerType.length > 0;
}

export function normalizeCalculateOffersResponse(raw: CalculateOffersResponse): CalculateOffersResponse {
  const currency = raw.currency || 'EUR';

  const buildFromTopLevel = (): OfferItem[] => {
    const list = Array.isArray(raw.items) ? [...raw.items] : [];
    return aggregateOfferItems(list).filter(isPurchasableOfferItem);
  };

  let items = buildFromTopLevel();

  if (items.length === 0 && Array.isArray(raw.people)) {
    items = aggregateOfferItems(collectItemsFromPeople(raw.people)).filter(isPurchasableOfferItem);
  }

  const total = items.reduce((sum, i) => sum + Math.max(0, i.price), 0);

  const trimmedImage = typeof raw.image === 'string' ? raw.image.trim() : '';
  const image = trimmedImage !== '' ? trimmedImage : undefined;

  return {
    items,
    total,
    currency,
    people: raw.people,
    image,
  };
}
